import { useEffect } from 'react';
import { Scaling } from 'lucide-react';
import { Label } from '../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { formatUiScale, supportsUiScale, useUiScaleStore } from '../../store/uiScaleStore';

/**
 * Appearance row: how large the whole interface is drawn.
 *
 * A zoom factor rather than a font size, so it takes the icons, spacing and
 * fixed pixel sizes with it instead of only the text. Windows starts at 90%
 * because the app's metrics are macOS-shaped and read oversized next to the
 * Windows shell — see electron/uiScale.ts for the full reasoning.
 *
 * Hidden in browser mode, where there is no window to zoom.
 */
export function UiScaleSection() {
  const scale = useUiScaleStore((state) => state.scale);
  const steps = useUiScaleStore((state) => state.steps);
  const platformDefault = useUiScaleStore((state) => state.platformDefault);
  const setScale = useUiScaleStore((state) => state.setScale);
  const load = useUiScaleStore((state) => state.load);
  const { track } = useAnalytics();

  // The window may have been scaled in another session (it is persisted by the
  // main process), so the panel asks rather than trusting the launch payload.
  useEffect(() => {
    void load();
  }, [load]);

  if (!supportsUiScale()) return null;

  // Before `load` answers, offer at least the value in effect so the control is
  // never empty on first paint.
  const options = steps.length > 0 ? steps : [scale];

  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
      <div className="flex items-center gap-3">
        <Scaling className="h-5 w-5 text-muted-foreground" />
        <div className="grid gap-0.5">
          <Label className="cursor-default">Interface scale</Label>
          <div className="text-xs text-muted-foreground">
            How large the whole app is drawn — text, icons and spacing together.
          </div>
        </div>
      </div>
      <Select
        value={String(scale)}
        onValueChange={(value) => {
          const next = Number(value);
          if (!Number.isFinite(next) || next === scale) return;
          void setScale(next);
          // A mode switch reports what was picked, not `enabled` — the same
          // shape as `feature:theme_mode_changed` next to it. The label is
          // low-cardinality by construction (a fixed list of steps).
          track('feature:ui_scale_changed', { scale: formatUiScale(next) });
        }}
      >
        {/* Sized to its value, at least 7rem: a fixed 7rem cut "90% (default)" — the Windows default — to "90%…". */}
        <SelectTrigger className="ml-6 w-auto min-w-28 shrink-0 gap-2 whitespace-nowrap" size="sm" data-testid="ui-scale-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((step) => (
            <SelectItem key={step} value={String(step)} data-testid={`ui-scale-${step}`}>
              {formatUiScale(step)}
              {step === platformDefault ? ' (default)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
